import React, { useEffect, useRef, useState } from 'react';
import katex from 'katex';
import DOMPurify from 'dompurify';
import 'katex/dist/katex.min.css';

export interface RichViewProps {
  html?: string;
  showMore?: boolean;
}

export const RichView = ({ html = '', showMore = false }: RichViewProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sanitizedHtml, setSanitizedHtml] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!html) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    // Handle inline math: <span data-type="math-inline" formula="..." />
    wrapper
      .querySelectorAll('span[data-type="math-inline"]')
      .forEach((span) => {
        const formula = span.getAttribute('formula') || '';
        try {
          const rendered = katex.renderToString(formula, {
            throwOnError: false,
          });
          const renderedSpan = document.createElement('span');
          renderedSpan.innerHTML = rendered;
          span.replaceWith(renderedSpan);
        } catch (err) {
          console.error('Inline math render error:', err);
        }
      });

    // Handle block math: <div data-type="math-block" formula="..." />
    wrapper.querySelectorAll('div[data-type="math-block"]').forEach((div) => {
      const formula = div.getAttribute('formula') || '';
      try {
        const rendered = katex.renderToString(formula, {
          throwOnError: false,
          displayMode: true,
        });
        const renderedDiv = document.createElement('div');
        renderedDiv.innerHTML = rendered;
        div.replaceWith(renderedDiv);
      } catch (err) {
        console.error('Block math render error:', err);
      }
    });

    setSanitizedHtml(DOMPurify.sanitize(wrapper.innerHTML));
  }, [html]);

  return (
    <div
      className="rich-editor prose prose-sm sm:prose-base max-w-none text-gray-900 leading-relaxed"
      style={{ fontSize: 'inherit' }}
    >
      <div
        className="relative"
        ref={containerRef}
        style={{
          maxHeight: showMore && !isExpanded ? '132px' : 'none',
          overflow: showMore && !isExpanded ? 'hidden' : 'visible',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      {showMore && !isExpanded && (
        <div
          className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, white 0%, transparent 100%)',
          }}
        />
      )}
      {showMore && (
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          {isExpanded ? 'View less' : 'View more'}
        </button>
      )}
    </div>
  );
};
